/**
 * Sagas for the WAC tutorial
 */

import { showAlert } from 'phoenix2/state/alert/sagas';
import { getFeatureFlag } from 'phoenix2/state/feature-flags/selectors';
import { LESSON, SOLO } from 'phoenix2/state/session/session-types';
import { setTutorialsCompleted } from 'phoenix2/state/tutorial/actions';
import { WAC_TUTORIAL_KEY } from 'phoenix2/state/tutorial/tutorial-reducers/wac';
import { VIDEO_TYPE_TUTORIAL } from 'phoenix2/state/video/video';
import { call, put, select } from 'redux-saga/effects';
import { FEATURE_FLAG_ONBOARDING_VIDEO } from '../../feature-flags';
import { getSessionType } from '../../session/selectors';
import { getTutorialState } from '../selectors';
import { ONBOARDING_VIDEO_TUTORIAL_KEY } from '../tutorial-reducers/onboarding-video';
import { QUIZ_TASK_TYPES } from 'phoenix2/utils/quiz.js';
import { getTask } from 'phoenix2/state/content/selectors_ts';
import { getIsPracticeHomework } from 'phoenix2/state/practice/selectors';
import { completeTrainingTask } from 'phoenix2/state/training/actions';
import { StudentExperienceTrainingStep } from 'phoenix2/content/training/training-modules';
import {
    getCurrentModule,
    getStudentExperienceTrainingModuleName,
} from 'phoenix2/state/training/selectors';
import { isFTQQuizComplete, isFTQQuizSkipped, isFTQTask } from 'phoenix2/utils/ftqtask';
import { getTaskState } from 'phoenix2/state/progress/selectors';

const ONBOARDING_VIDEO_ASSET_ID = 'faqs/how_to_bookwork_004';

const disableVideoTaskTypes = [...QUIZ_TASK_TYPES];

/**
 * Triggers the onboarding video tutorial, if the user has not completed
 * either the onboarding video tutorial or the WAC tutorial.
 * When the user closes the tutorial after watching the whole video, complete
 * the tutorial. If there is an error loading the video, continue without
 * completing the tutorial.
 */
export function* triggerOnBoardingVideoIfRequired(taskItem) {
    // Return if the onboarding video is disabled by the feature flag:
    // (If flag not present, do not disable):
    const onboardingVideoFeatureFlag = yield select(getFeatureFlag, FEATURE_FLAG_ONBOARDING_VIDEO);

    // Note, if undefined, we want to enable the video:
    if (onboardingVideoFeatureFlag === false) {
        return;
    }
    // Don't enable the video for a certain task types
    const taskId = taskItem && taskItem.get('task_id');
    const task = yield select(getTask, taskId);
    if (disableVideoTaskTypes.includes(task.get('task_type'))) {
        return;
    }

    const taskState = yield select(getTaskState, taskId);

    // Don't enable the onboarding video if the user is in an unfinished fast
    // track quiz, unless they are skipping it
    if (isFTQTask(task) && !isFTQQuizComplete(taskState) && !isFTQQuizSkipped(taskState)) {
        return;
    }

    // Don't enable the video if the student is in a lesson or in indy
    const sessionType = yield select(getSessionType);
    if (sessionType === LESSON || sessionType === SOLO) {
        return;
    }

    // Check if the onboarding video tutorial is active
    const onboardingTutorialState = yield select(getTutorialState, ONBOARDING_VIDEO_TUTORIAL_KEY);

    if (!onboardingTutorialState || !onboardingTutorialState.get('active')) {
        return;
    }

    // Check if the WAC tutorial has been completed. If so, don't show the
    // onboarding video. This is how we stop current users from seeing the
    // onboarding video on release.
    const wacTutorialState = yield select(getTutorialState, WAC_TUTORIAL_KEY);

    if (wacTutorialState && wacTutorialState.get('completed')) {
        return;
    }

    const isPracticeHw = yield select(getIsPracticeHomework);

    let hasVideoFinished = false;

    const closeReason = yield call(showAlert, {
        type: 'video',
        title: 'Watch this video before you begin',
        noDefaultButton: true,
        message: {
            videoId: ONBOARDING_VIDEO_ASSET_ID,
            skillId: undefined,
            activityIndex: undefined,
            skipToFinalFrame: false,
            videoType: VIDEO_TYPE_TUTORIAL,
            disableSeek: true,
            disableSkipButton: !isPracticeHw,
            showSubtitles: true,
            buttonText: 'Continue to question',
            onReachedVideoEnd: () => (hasVideoFinished = true),
        },
        className: 'alert-box-video onboarding-video',
        // Allows the alert to take up the whole screen with no border for small
        // devices:
        allowFullscreen: true,
        // Prevent the user closing the video with the top-right X button or
        // Escape shortcut
        preventDismiss: true,
    });

    // When the user has watched the video all the way through, complete the
    // onboarding video tutorial, and continue to task. Don't complete if the
    // video errored
    if (closeReason === 'close') {
        yield put(setTutorialsCompleted([ONBOARDING_VIDEO_TUTORIAL_KEY], true));

        const module = yield select(getCurrentModule);

        if (module !== undefined) {
            yield call(showAlert, {
                title: 'Completing Bookwork Checks',
                type: 'trainingBookworkChecks',
                buttons: [{ text: 'Continue training', key: 'close' }],
            });
        }
    }

    if (hasVideoFinished) {
        yield put(
            completeTrainingTask({
                moduleName: yield select(getStudentExperienceTrainingModuleName),
                stepID: StudentExperienceTrainingStep.BOOKWORK_VIDEO_WATCHED,
            }),
        );
    }
}
